import { List, Loader } from '@mantine/core';
// @ts-ignore
import {
  IconArrowRight,
  IconFile,
  IconFileSpreadsheet,
  IconFileTypeDoc,
  IconFileTypePpt,
  IconPdf,
  IconTrash,
} from '@tabler/icons-react';
import { useState } from 'react';
import {
  useDeleteSupplierRequestDocMutation,
  useGetSupplierRequestDocMutation,
  useUploadSupplierRequestDocMutation,
} from '../../../api';
import { Button, Dropzone } from '../../../ui';
import { useSupplierRequestWizardContext } from './context';

const getIcon = (file: string) => {
  if (file === '') {
    return undefined;
  }
  if (file.endsWith('.pdf')) {
    return <IconPdf size={16} />;
  }
  if (file.endsWith('.xls') || file.endsWith('.xlsx')) {
    return <IconFileSpreadsheet size={16} />;
  }

  if (file.endsWith('.doc') || file.endsWith('.docx')) {
    return <IconFileTypeDoc size={16} />;
  }
  if (file.endsWith('.ppt') || file.endsWith('.pptx')) {
    return <IconFileTypePpt size={16} />;
  }
  return <IconFile size={16} />;
};

export const Initial = () => {
  const { setFlow, setCurrentStep, supplierRequestInput, handleChangeValue } =
    useSupplierRequestWizardContext();
  const [loading, setLoading] = useState(false);
  const [getSupplierRequestDoc] = useGetSupplierRequestDocMutation();
  const [deleteSupplierRequestDoc, { isLoading: deleteLoading }] =
    useDeleteSupplierRequestDocMutation();
  const [uploadSupplierRequestDoc] = useUploadSupplierRequestDocMutation();
  const hasDocuments = supplierRequestInput.documents.length > 0;
  const handleFileUpload = async (file: File | null) => {
    setLoading(true);
    if (file) {
      const { data } = await uploadSupplierRequestDoc({
        contentType: file.type,
        fileName: file.name,
      });
      const { key, presignedUrl } = data!;
      const upload = await fetch(presignedUrl, {
        method: 'PUT',
        body: file,
      });
      if (upload.ok) {
        handleChangeValue('documents', [
          ...supplierRequestInput.documents,
          {
            fileName: file.name,
            key,
            contentType: file.type,
          },
        ]);
      }
    }
    setLoading(false);
  };

  const handleFileRemove = async (file: string) => {
    const fileToDelete = supplierRequestInput.documents.find(
      (f: any) => f.fileName === file
    );
    if (fileToDelete) {
      const res = await deleteSupplierRequestDoc(fileToDelete.key);
      handleChangeValue &&
        handleChangeValue(
          'documents',
          supplierRequestInput.documents.filter(
            (f: any) => f.fileName !== fileToDelete.fileName
          )
        );
    }
  };

  const handleViewFile = async (key: string) => {
    const res = await getSupplierRequestDoc(key);
    window.open(res.data?.presignedUrl);
  };

  return (
    <>
      <div className="flex flex-col items-center mb-2">
        <div className="py-2">
          <p className="text-3xl font-bold text-primary-600">
            Choose Your Path
          </p>
        </div>

        <div className="py-2">
          <p className="text-xl text-text-secondary">
            How would you like to create your request?
          </p>
        </div>
      </div>
      <div className="flex gap-8 pt-12">
        <div className="flex flex-col gap-4 flex-1 border-dashed border-primary-500 border rounded-2xl px-4">
          <div className="flex flex-col gap-2 mx-8 mt-8 mb-2">
            <span className="text-2xl font-bold text-center">
              Upload A Document
            </span>
            <span className="text-base font-normal pt-8">
              Upload your own purchasing document. Our system will parse the
              information, allowing you to fill in any remaining details and
              customize your request as needed.
            </span>
          </div>
          <div className="pb-14">
            <Dropzone
              disabled={supplierRequestInput.documents.length > 0}
              isLoading={loading}
              displayInstructions
              handleUpload={handleFileUpload}
            />
            <List spacing="xs" size="sm" center className="mx-8">
              {supplierRequestInput.documents.map((doc: any) => (
                <List.Item icon={getIcon(doc.fileName)} key={doc.key}>
                  <div className=" cursor-pointer flex flex-row items-center">
                    <a onClick={() => handleViewFile(doc.key)}>
                      <span className="ml-2">{doc.fileName}</span>
                    </a>
                    {deleteLoading ? (
                      <Loader className="ml-4" size="xs" color="red" />
                    ) : (
                      <IconTrash
                        className="ml-4 cursor-pointer"
                        size={16}
                        color="red"
                        onClick={() => handleFileRemove(doc.fileName)}
                      />
                    )}
                  </div>
                </List.Item>
              ))}
            </List>
          </div>

          <div className="relative h-full w-full">
            <div className="absolute bottom-0 w-[inherit]">
              <div className="mx-14 mb-4">
                <Button
                  text="Continue"
                  variant={
                    !hasDocuments || loading || deleteLoading
                      ? 'disabled'
                      : 'primary'
                  }
                  suffixIcon={<IconArrowRight />}
                  onClick={() => {
                    setCurrentStep('PREFERENCES');
                    setFlow('upload');
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col justify-center">
          <p className="text-3xl font-bold text-primary-600">OR</p>
        </div>

        <div className="flex flex-col gap-4 flex-1 border-dashed border-primary-500 border rounded-2xl px-4">
          <div className="flex flex-col gap-2 m-8">
            <span className="text-2xl font-bold text-center">
              Supplier Request Wizard
            </span>
            <span className="text-base font-normal pt-8">
              Use our guided, step-by-step Supplier Request Wizard to provide
              detailed information about your buying needs.
            </span>
          </div>

          <div className="relative h-full w-full">
            <div className="absolute bottom-0 w-[inherit]">
              <div className="mx-14 mb-4">
                <Button
                  text="Continue"
                  variant={hasDocuments || loading ? 'disabled' : 'primary'}
                  suffixIcon={<IconArrowRight />}
                  onClick={() => {
                    setCurrentStep('REQUIREMENTS');
                    setFlow('wizard');
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
